import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "src/common/state/store";
import { fetchAgents, RagAgent } from "../api/ragApi";

type RagAgentsStatus = "idle" | "loading" | "loaded" | "error";

interface IRagState {
  agents: RagAgent[];
  status: RagAgentsStatus;
  error: string | null;
}

const initialState: IRagState = {
  agents: [],
  status: "idle",
  error: null,
};

export const fetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/fetchAgents",
  async () => fetchAgents(),
  {
    // Skip if a fetch is already in flight or the list is fresh. Consumers can
    // dispatch this on every mount without worrying about duplicate requests.
    condition: (_, { getState }) => {
      const status = getState().rag.status;
      return status !== "loading" && status !== "loaded";
    },
  },
);

export const ragSlice = createSlice({
  name: "rag",
  initialState,
  reducers: {
    invalidateAgents: (state) => {
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentsThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAgentsThunk.fulfilled, (state, { payload }) => {
        state.agents = payload;
        state.status = "loaded";
      })
      .addCase(fetchAgentsThunk.rejected, (state, { error }) => {
        state.status = "error";
        state.error = error.message ?? "Failed to fetch RAG agents";
      });
  },
});

export const ragReducer = ragSlice.reducer;
export const { invalidateAgents } = ragSlice.actions;

export const getRagAgents = (state: RootState) => state.rag.agents;
export const getRagAgentsStatus = (state: RootState) => state.rag.status;
